<template>
    <canvas id="viewer" ref="viewer" :width="width" :height="height"/>
</template>

<script>

export default {
    name: "ViewComponent",
    props: {
        width: {type: Number, default: 640},
        height: {type: Number, default: 480},
        component: {type: Object, default: null},
        vertexThreshold: {type: Number, default: 10},
    },
    mounted() {
        if (this.component && ('loops' in this.component)) {
            this.drawComponent();
        }
    },
    methods: {
        drawComponent() {
            function arc(arccurve, color = null, lineWidth = null) {
                ctx.beginPath();
                ctx.arc(
                    arccurve.center.x,
                    arccurve.center.y,
                    arccurve.radius,
                    arccurve.start_angle,
                    arccurve.end_angle
                );
                ctx.strokeStyle = color ? color : arcColor;
                ctx.lineWidth = lineWidth ? lineWidth : arcLineWidth;
                ctx.stroke();
            }

            function allowanceDeductionMarker(position, allowanceDeduction) {
                if (allowanceDeduction) {
                    let color = allowanceDeduction > 0 ? allowanceColor : deductionColor
                    ctx.beginPath();
                    ctx.arc(
                        position.x,
                        position.y,
                        5 / scale,
                        0,
                        2 * Math.PI
                    );
                    ctx.strokeStyle = color;
                    ctx.fillStyle = color;
                    ctx.fill();
                }
            }

            function line(position1, position2, color = null, lineWidth = null) {
                ctx.beginPath();
                ctx.moveTo(position1.x, position1.y);
                ctx.lineTo(position2.x, position2.y);
                ctx.strokeStyle = color ? color : standardColor;
                ctx.lineWidth = lineWidth ? lineWidth : standardLineWidth;
                ctx.stroke();
            }

            const canvas = this.$refs['viewer'];
            const ctx = canvas.getContext("2d");

            // get min and max values for component vertices
            let minX = Number.POSITIVE_INFINITY, minY = Number.POSITIVE_INFINITY, maxX = Number.NEGATIVE_INFINITY, maxY = Number.NEGATIVE_INFINITY;
            this.component.loops.forEach(loopData => {
                loopData.loop.forEach(item => {
                    if (item.type === 'ArcCurve') {
                        minX = Math.min(minX, parseFloat(item.center.x) - parseFloat(item.radius));
                        minY = Math.min(minY, parseFloat(item.center.y) - parseFloat(item.radius));
                        maxX = Math.max(maxX, parseFloat(item.center.x) + parseFloat(item.radius));
                        maxY = Math.max(maxY, parseFloat(item.center.y) + parseFloat(item.radius));
                    } else if (item.type === 'Vertex') {
                        minX = Math.min(minX, parseFloat(item.position.x));
                        minY = Math.min(minY, parseFloat(item.position.y));
                        maxX = Math.max(maxX, parseFloat(item.position.x));
                        maxY = Math.max(maxY, parseFloat(item.position.y));
                    }
                });
            });
            const componentWidth = maxX - minX;
            const componentHeight = maxY - minY;

            // get scale factor while maintaining aspect ratio
            const margin = 15;
            const availableWidth = ctx.canvas.width - 2 * margin;
            const availableHeight = ctx.canvas.height - 2 * margin;
            const scale = Math.min(availableWidth / componentWidth, availableHeight / componentHeight);

            // reset canvas
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            // move to center of canvas
            ctx.translate(canvas.width / 2, canvas.height / 2);
            // apply scale
            ctx.scale(scale, -scale);
            // set center to (0,0)
            const centerX = minX + (componentWidth / 2);
            const centerY = minY + (componentHeight / 2);
            ctx.translate(-centerX, -centerY);

            const warningColor = "Orange";
            const arcColor = "Blue";
            const arcLineWidth = 1 / scale;
            const foldColor = "Red";
            const foldLineWidth = 2 / scale;
            const standardColor = "DarkGray";
            const standardLineWidth = 1 / scale;
            const allowanceColor = "LimeGreen";
            const deductionColor = "Red";

            this.component.loops.forEach(loopData => {
                let color = !loopData.outer && loopData.loop.length > this.vertexThreshold ? warningColor : null;
                let prevItem = null;
                loopData.loop.forEach(item => {
                    if (item.type === 'ArcCurve') {
                        arc(item, color);
                        prevItem = null;
                    } else if (item.type === 'Vertex') {
                        if (prevItem) {
                            line(prevItem.position, item.position, color);
                        }
                        prevItem = item;
                    }
                });
                if (prevItem && loopData.loop[0].type === 'Vertex') {
                    line(prevItem.position, loopData.loop[0].position, color);
                }
            });

            if ('folds' in this.component) {
                this.component.folds.forEach(fold => {
                    fold.markers.forEach(marker => {
                        line(marker.pt1, marker.pt2, foldColor, foldLineWidth);
                        allowanceDeductionMarker(marker.pt1, fold.allowance_deduction);
                    });
                });
            }
        },
    }
}
</script>

<style scoped>
#viewer {
    outline: 1px solid #DFDFDF;
    position: sticky;
    top: 105px;
}
</style>
